import { formatNumber, getTranslate, onLocaleChange, setLocalizedAttribute, setLocalizedText } from "../i18n";

const STYLE_ID = "wavemaker-progress-report-styles";
const OVERLAY_ROOT_ID = "wavemaker-progress-report-root";
const DIALOG_MIN_WIDTH = 660;
const DIALOG_VIEWPORT_FIT_RATIO = 0.92;

export const PROGRESS_REPORT_MODAL_ID = "progress-report-modal";

const formatDuration = (totalSeconds) => {
  const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  const pad = (value) => String(value).padStart(2, "0");
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
};

const ensureStyles = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    #${OVERLAY_ROOT_ID} {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 50;
      display: block;
      font-family: "Avenir Next", "Avenir", "Helvetica Neue", Arial, sans-serif;
    }

    #${OVERLAY_ROOT_ID}.has-modal {
      pointer-events: auto;
    }

    #${OVERLAY_ROOT_ID} .progress-report-modal {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: radial-gradient(circle at 50% 30%, rgba(53, 64, 70, 0.7), rgba(26, 32, 35, 0.72));
      opacity: 0;
      transform: scale(0.98);
      transition: opacity 160ms ease, transform 180ms ease;
    }

    #${OVERLAY_ROOT_ID} .progress-report-modal.is-visible {
      opacity: 1;
      transform: scale(1);
    }

    .progress-report-dialog {
      width: ${DIALOG_MIN_WIDTH}px;
      height: auto;
      padding: 22px 26px;
      border-radius: 22px;
      background: linear-gradient(160deg, rgba(252, 254, 253, 0.98), rgba(233, 239, 236, 0.98));
      border: 1px solid rgba(255, 255, 255, 0.65);
      box-shadow: 0 26px 70px rgba(8, 14, 12, 0.45);
      color: #1f2d28;
      display: flex;
      flex-direction: column;
      gap: 12px;
      position: relative;
      overflow: hidden;
      transform-origin: center center;
      will-change: transform;
    }

    .progress-report-close {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 36px;
      height: 36px;
      border-radius: 12px;
      border: 1px solid rgba(34, 49, 44, 0.16);
      background: rgba(255, 255, 255, 0.6);
      color: #1f2d28;
      font-size: 22px;
      line-height: 1;
      cursor: pointer;
      transition: transform 140ms ease, background 160ms ease, border-color 160ms ease;
    }

    .progress-report-close:hover {
      background: rgba(255, 255, 255, 0.9);
      border-color: rgba(34, 49, 44, 0.28);
      transform: translateY(-1px);
    }

    .progress-report-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-right: 40px;
    }

    .progress-report-title {
      margin: 0;
      font-size: 26px;
      color: #22312c;
      letter-spacing: -0.01em;
    }

    .progress-report-name-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .progress-report-name-label {
      font-size: 14px;
      font-weight: 600;
      color: #2b3a34;
      white-space: nowrap;
    }

    .progress-report-name-input {
      flex: 1;
      padding: 10px 12px;
      border-radius: 8px;
      border: 1px solid rgba(34, 49, 44, 0.24);
      font-size: 16px;
      background: linear-gradient(180deg, #ffffff 0%, #eef4f1 100%);
      color: #21312b;
      outline: none;
    }

    .progress-report-name-input:focus-visible {
      outline: 2px solid rgba(65, 189, 215, 0.35);
      outline-offset: 2px;
    }

    .progress-report-name-input:disabled {
      background: rgba(232, 238, 235, 0.95);
      color: rgba(33, 49, 43, 0.72);
      cursor: not-allowed;
    }

    .progress-report-name-input.is-locked {
      font-size: 18px;
      font-weight: 500;
      color: #1f2c27;
      background-image: linear-gradient(180deg, rgb(255, 255, 255), rgb(255, 255, 255)),
        repeating-linear-gradient(135deg, #ffffff 0px, #ffffff 10px, #f4f6f5 10px, #f4f6f5 20px);
      background-size: 100% 100%, 28px 28px;
      background-blend-mode: multiply;
    }

    .progress-report-name-button {
      border: 2px solid rgba(34, 49, 44, 0.2);
      background: linear-gradient(150deg, #ffffff, #eef4f1);
      border-radius: 8px;
      padding: 8px 14px;
      font-size: 13px;
      font-weight: 700;
      color: #1f2d28;
      cursor: pointer;
      transition: transform 0.14s ease, border-color 0.16s ease, filter 0.16s ease;
      white-space: nowrap;
    }

    .progress-report-name-button-line {
      display: block;
      line-height: 1.1;
    }

    .progress-report-name-button-sub {
      display: block;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.01em;
      margin-top: 2px;
    }

    .progress-report-name-button:hover {
      border-color: rgba(65, 189, 215, 0.45);
      transform: translateY(-1px);
      filter: brightness(1.02);
    }

    .progress-report-name-button:not(:disabled) {
      background: #4fa4a1;
      color: #ffffff;
      border-color: rgba(79, 164, 161, 0.6);
    }

    .progress-report-name-button:not(:disabled):hover {
      filter: brightness(1.06);
    }

    .progress-report-name-button:active {
      transform: translateY(0);
    }

    .progress-report-name-button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      filter: none;
      transform: none;
    }

    .progress-report-name-button.is-hidden {
      display: none;
    }

    .progress-report-gate-message {
      display: none;
      font-size: 16px;
      font-weight: 600;
      color: rgba(33, 49, 43, 0.75);
      text-align: center;
      padding: 24px 16px;
      border-radius: 12px;
      border: 1px dashed rgba(34, 49, 44, 0.3);
      background: linear-gradient(160deg, rgba(238, 244, 241, 0.9), rgba(255, 255, 255, 0.9));
    }

    .progress-report-canvas-wrapper {
      flex: 0 0 auto;
      min-height: 0;
      display: flex;
      width: 100%;
    }

    .progress-report-canvas {
      width: 100%;
      height: 100%;
      display: block;
      border-radius: 16px;
      background: #f9fbfa;
    }

    .progress-report-content.is-awaiting-name .progress-report-canvas-wrapper,
    .progress-report-content.is-awaiting-name .progress-report-actions {
      display: none;
    }

    .progress-report-content.is-awaiting-name .progress-report-gate-message {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .progress-report-content.is-ready .progress-report-gate-message {
      display: none;
    }

    .progress-report-actions {
      display: flex;
      justify-content: center;
      gap: 12px;
      padding-top: 4px;
      flex-wrap: wrap;
    }

    .progress-report-action-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 2px solid rgba(34, 49, 44, 0.18);
      background: linear-gradient(150deg, #ffffff, #eef4f1);
      border-radius: 10px;
      padding: 12px 20px;
      font-size: 14px;
      font-weight: 700;
      color: #1f2d28;
      cursor: pointer;
      transition: transform 0.14s ease, border-color 0.16s ease, filter 0.16s ease;
      white-space: nowrap;
      min-width: 250px;
    }

    .progress-report-action-button:hover {
      border-color: rgba(65, 189, 215, 0.4);
      transform: translateY(-1px);
      filter: brightness(1.02);
    }

    .progress-report-action-button:focus-visible {
      outline: 2px solid rgba(65, 189, 215, 0.35);
      outline-offset: 2px;
    }

    .progress-report-action-button:active {
      transform: translateY(0);
    }

    .progress-report-action-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      filter: none;
      transform: none;
    }
  `;
  document.head.appendChild(style);
};

const getOverlayRoot = () => {
  if (typeof document === "undefined") return null;
  let root = document.getElementById(OVERLAY_ROOT_ID);
  if (!root) {
    root = document.createElement("div");
    root.id = OVERLAY_ROOT_ID;
    document.body.appendChild(root);
  }
  return root;
};

export class ProgressReportModal {
  constructor(options = {}) {
    this.onNameCommit = options.onNameCommit;
    this.modal = null;
    this.snapshot = null;
    this.nameLocked = false;
    this.isSaving = false;
    this.copyFeedbackTimeout = null;
    this.cleanup = [];
    this.lastNameInputValue = "";
    this.overlayRoot = null;
    this.localeUnsubscribe = null;

    ensureStyles();
    this.overlayRoot = getOverlayRoot();

    const dialog = this.buildDialog();
    this.dialog = dialog;
    this.content = dialog;

    this.clipboardSupported = this.checkClipboardSupport();
    if (this.copyButton) {
      this.copyButton.disabled = !this.clipboardSupported;
      this.copyButton.setAttribute("aria-disabled", this.clipboardSupported ? "false" : "true");
    }

    this.content.classList.add("is-awaiting-name");
    this.bindEvents();
    this.updateNameButtonState();
    this.updateReportVisibility();

    this.localeUnsubscribe = onLocaleChange(() => {
      this.applyTranslations();
      void this.renderReportCanvas();
    });
  }

  destroy() {
    this.cleanup.forEach((fn) => fn());
    this.cleanup = [];
    if (this.localeUnsubscribe) {
      this.localeUnsubscribe();
      this.localeUnsubscribe = null;
    }
    if (this.copyFeedbackTimeout && typeof window !== "undefined") {
      window.clearTimeout(this.copyFeedbackTimeout);
      this.copyFeedbackTimeout = null;
    }
    this.close();
    this.dialog = null;
    this.content = null;
    this.modal = null;
  }

  open() {
    if (!this.overlayRoot || !this.dialog) return;
    if (!this.modal) {
      const modal = document.createElement("div");
      modal.className = "progress-report-modal";
      modal.dataset.modalId = PROGRESS_REPORT_MODAL_ID;
      modal.appendChild(this.dialog);
      const handler = (event) => {
        if (event.target === modal) {
          this.close();
        }
      };
      modal.addEventListener("pointerdown", handler);
      this.cleanup.push(() => modal.removeEventListener("pointerdown", handler));
      this.modal = modal;
    }
    this.overlayRoot.appendChild(this.modal);
    requestAnimationFrame(() => {
      this.modal?.classList.add("is-visible");
      this.overlayRoot?.classList.add("has-modal");
      this.updateDialogScale();
    });
    this.updateNameButtonState();
    this.updateReportVisibility();
    void this.renderReportCanvas();
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove("is-visible");
    this.modal.remove();
    this.overlayRoot?.classList.remove("has-modal");
  }

  setSnapshot(snapshot) {
    this.snapshot = snapshot;
    const name = snapshot?.playerName ?? "";
    this.setName(name);
    void this.renderReportCanvas();
    this.updateDialogScale();
  }

  buildDialog() {
    const dialog = document.createElement("div");
    dialog.className = "progress-report-dialog progress-report-content";

    const closeBtn = document.createElement("button");
    closeBtn.className = "progress-report-close";
    closeBtn.type = "button";
    setLocalizedAttribute(closeBtn, "aria-label", "progressReport.closeAria");
    closeBtn.textContent = "x";
    dialog.appendChild(closeBtn);
    this.closeButton = closeBtn;

    const header = document.createElement("div");
    header.className = "progress-report-header";
    const title = document.createElement("h2");
    title.className = "progress-report-title";
    setLocalizedText(title, "progressReport.title");
    header.appendChild(title);
    dialog.appendChild(header);
    this.header = header;

    const nameRow = document.createElement("div");
    nameRow.className = "progress-report-name-row";
    const nameLabel = document.createElement("label");
    nameLabel.className = "progress-report-name-label";
    nameLabel.setAttribute("for", "progress-report-name-input");
    setLocalizedText(nameLabel, "progressReport.nameLabel");
    const nameInput = document.createElement("input");
    nameInput.className = "progress-report-name-input";
    nameInput.id = "progress-report-name-input";
    nameInput.type = "text";
    setLocalizedAttribute(nameInput, "placeholder", "progressReport.namePlaceholder");
    setLocalizedAttribute(nameInput, "aria-label", "progressReport.nameAria");
    nameInput.autocomplete = "off";
    nameInput.autocorrect = "off";
    nameInput.autocapitalize = "off";
    nameInput.spellcheck = false;
    nameInput.inputMode = "text";
    const nameButton = document.createElement("button");
    nameButton.className = "progress-report-name-button";
    nameButton.type = "button";
    nameButton.disabled = true;
    nameButton.setAttribute("aria-disabled", "true");
    const buttonLine = document.createElement("span");
    buttonLine.className = "progress-report-name-button-line";
    setLocalizedText(buttonLine, "progressReport.nameButton");
    const buttonSub = document.createElement("span");
    buttonSub.className = "progress-report-name-button-sub";
    setLocalizedText(buttonSub, "progressReport.nameButtonSub");
    nameButton.appendChild(buttonLine);
    nameButton.appendChild(buttonSub);
    nameRow.appendChild(nameLabel);
    nameRow.appendChild(nameInput);
    nameRow.appendChild(nameButton);
    dialog.appendChild(nameRow);

    const gateMessage = document.createElement("div");
    gateMessage.className = "progress-report-gate-message";
    setLocalizedText(gateMessage, "progressReport.gateMessage");
    dialog.appendChild(gateMessage);
    this.nameRow = nameRow;
    this.nameInput = nameInput;
    this.nameButton = nameButton;

    const canvasWrapper = document.createElement("div");
    canvasWrapper.className = "progress-report-canvas-wrapper";
    const canvas = document.createElement("canvas");
    canvas.className = "progress-report-canvas";
    canvas.setAttribute("role", "img");
    setLocalizedAttribute(canvas, "aria-label", "progressReport.canvasAria");
    canvasWrapper.appendChild(canvas);
    dialog.appendChild(canvasWrapper);
    this.reportWrapper = canvasWrapper;
    this.reportCanvas = canvas;

    const actions = document.createElement("div");
    actions.className = "progress-report-actions";
    const saveButton = document.createElement("button");
    saveButton.className = "progress-report-action-button";
    saveButton.type = "button";
    setLocalizedText(saveButton, "progressReport.saveButton");
    const copyButton = document.createElement("button");
    copyButton.className = "progress-report-action-button";
    copyButton.type = "button";
    setLocalizedText(copyButton, "progressReport.copyButton");
    actions.appendChild(saveButton);
    actions.appendChild(copyButton);
    dialog.appendChild(actions);
    this.saveButton = saveButton;
    this.copyButton = copyButton;

    return dialog;
  }

  applyTranslations() {
    if (!this.dialog) {
      return;
    }
    if (this.closeButton) {
      setLocalizedAttribute(this.closeButton, "aria-label", "progressReport.closeAria");
    }
    const title = this.dialog.querySelector(".progress-report-title");
    if (title) {
      setLocalizedText(title, "progressReport.title");
    }
    const nameLabel = this.dialog.querySelector(".progress-report-name-label");
    if (nameLabel) {
      setLocalizedText(nameLabel, "progressReport.nameLabel");
    }
    if (this.nameInput) {
      setLocalizedAttribute(this.nameInput, "placeholder", "progressReport.namePlaceholder");
      setLocalizedAttribute(this.nameInput, "aria-label", "progressReport.nameAria");
    }
    const buttonLine = this.dialog.querySelector(".progress-report-name-button-line");
    if (buttonLine) {
      setLocalizedText(buttonLine, "progressReport.nameButton");
    }
    const buttonSub = this.dialog.querySelector(".progress-report-name-button-sub");
    if (buttonSub) {
      setLocalizedText(buttonSub, "progressReport.nameButtonSub");
    }
    const gateMessage = this.dialog.querySelector(".progress-report-gate-message");
    if (gateMessage) {
      setLocalizedText(gateMessage, "progressReport.gateMessage");
    }
    if (this.reportCanvas) {
      setLocalizedAttribute(this.reportCanvas, "aria-label", "progressReport.canvasAria");
    }
    if (this.saveButton) {
      setLocalizedText(this.saveButton, "progressReport.saveButton");
    }
    if (this.copyButton) {
      setLocalizedText(this.copyButton, "progressReport.copyButton");
    }
  }

  bindEvents() {
    if (this.closeButton) {
      const handler = () => this.close();
      this.closeButton.addEventListener("click", handler);
      this.cleanup.push(() => this.closeButton?.removeEventListener("click", handler));
    }
    if (this.nameInput) {
      this.lastNameInputValue = this.nameInput.value;
      const handler = (event) => this.handleNameInput(event);
      this.nameInput.addEventListener("input", handler);
      this.cleanup.push(() => this.nameInput?.removeEventListener("input", handler));
      const keyHandler = (event) => {
        event.stopPropagation();
      };
      this.nameInput.addEventListener("keydown", keyHandler);
      this.nameInput.addEventListener("keyup", keyHandler);
      this.cleanup.push(() => this.nameInput?.removeEventListener("keydown", keyHandler));
      this.cleanup.push(() => this.nameInput?.removeEventListener("keyup", keyHandler));
      const beforeInputHandler = (event) => this.handleNameBeforeInput(event);
      this.nameInput.addEventListener("beforeinput", beforeInputHandler);
      this.cleanup.push(() =>
        this.nameInput?.removeEventListener("beforeinput", beforeInputHandler),
      );
    }
    if (this.nameButton) {
      const handler = () => this.handleNameCommit();
      this.nameButton.addEventListener("click", handler);
      this.cleanup.push(() => this.nameButton?.removeEventListener("click", handler));
    }
    if (this.saveButton) {
      const handler = () => {
        void this.handleSaveImage();
      };
      this.saveButton.addEventListener("click", handler);
      this.cleanup.push(() => this.saveButton?.removeEventListener("click", handler));
    }
    if (this.copyButton) {
      const handler = () => {
        void this.handleCopyImage();
      };
      this.copyButton.addEventListener("click", handler);
      this.cleanup.push(() => this.copyButton?.removeEventListener("click", handler));
    }
    if (typeof window !== "undefined") {
      const handler = () => {
        void this.renderReportCanvas();
        this.updateDialogScale();
      };
      window.addEventListener("resize", handler);
      this.cleanup.push(() => window.removeEventListener("resize", handler));
    }
  }

  handleNameCommit() {
    if (this.nameLocked || !this.nameInput) return;
    const trimmed = this.nameInput.value.trim();
    if (!trimmed) return;
    if (typeof this.onNameCommit === "function") {
      this.onNameCommit(trimmed);
    }
    this.setName(trimmed);
  }

  setName(name) {
    if (!this.nameInput || !this.nameButton) return;
    const trimmed = String(name ?? "").trim();
    if (!trimmed && !this.nameLocked) {
      this.updateNameButtonState();
      return;
    }
    const shouldLock = Boolean(trimmed) || this.nameLocked;
    const nextValue = trimmed || this.nameInput.value.trim();
    this.nameLocked = shouldLock;
    if (nextValue) {
      this.nameInput.value = nextValue;
    }
    this.nameInput.readOnly = shouldLock;
    this.nameInput.disabled = shouldLock;
    this.nameInput.setAttribute("aria-readonly", shouldLock ? "true" : "false");
    this.nameInput.classList.toggle("is-locked", shouldLock);
    this.nameButton.classList.toggle("is-hidden", shouldLock);
    this.lastNameInputValue = this.nameInput.value;
    this.updateReportVisibility();
    if (shouldLock) {
      void this.renderReportCanvas();
      this.nameButton.disabled = true;
      this.nameButton.setAttribute("aria-disabled", "true");
    } else {
      this.updateNameButtonState();
    }
    this.updateDialogScale();
  }

  updateNameButtonState() {
    if (!this.nameButton || !this.nameInput) return;
    if (this.nameLocked) {
      this.nameButton.disabled = true;
      this.nameButton.setAttribute("aria-disabled", "true");
      return;
    }
    const trimmed = this.nameInput.value.trim();
    const disabled = trimmed.length === 0;
    this.nameButton.disabled = disabled;
    this.nameButton.setAttribute("aria-disabled", disabled ? "true" : "false");
    this.updateActionButtonsAvailability();
  }

  handleNameInput(event) {
    if (!this.nameInput) return;
    if (event instanceof InputEvent) {
      this.preventDoubleSpacePeriod(event);
    }
    this.lastNameInputValue = this.nameInput.value;
    this.updateNameButtonState();
  }

  updateReportVisibility() {
    if (!this.content) return;
    const ready = this.nameLocked;
    this.content.classList.toggle("is-awaiting-name", !ready);
    this.content.classList.toggle("is-ready", ready);
    this.updateActionButtonsAvailability();
    this.updateDialogScale();
  }

  updateActionButtonsAvailability() {
    const ready = this.nameLocked && Boolean(this.snapshot);
    if (this.saveButton) {
      const disabled = !ready || this.isSaving;
      this.saveButton.disabled = disabled;
      this.saveButton.setAttribute("aria-disabled", disabled ? "true" : "false");
    }
    if (this.copyButton) {
      const disabled = !ready || this.isSaving || !this.clipboardSupported;
      this.copyButton.disabled = disabled;
      this.copyButton.setAttribute("aria-disabled", disabled ? "true" : "false");
    }
  }

  handleNameBeforeInput(event) {
    if (!this.nameInput || !(event instanceof InputEvent)) return;
    if (!event.data || !event.inputType.startsWith("insert")) return;
    const data = event.data;
    if (!data.startsWith(".")) return;
    const selectionStart = this.nameInput.selectionStart;
    const selectionEnd = this.nameInput.selectionEnd;
    if (selectionStart === null || selectionEnd === null || selectionStart !== selectionEnd) {
      return;
    }
    const prevChar = this.nameInput.value.charAt(selectionStart - 1);
    if (prevChar !== " ") return;
    event.preventDefault();
    this.insertTextAtCursor(" ");
  }

  preventDoubleSpacePeriod(event) {
    if (!this.nameInput) return;
    const value = this.nameInput.value;
    const lastValue = this.lastNameInputValue;
    if (!lastValue) return;
    const cursor = this.nameInput.selectionStart ?? value.length;
    if (cursor < 2) return;
    if (value.slice(cursor - 2, cursor) !== ". ") return;
    const priorChar = lastValue.charAt(cursor - 2);
    const lengthIncreased = value.length === lastValue.length + 1;
    const dataHasPeriod = (event.data ?? "").includes(".");
    if (priorChar !== " " || (!dataHasPeriod && !lengthIncreased)) return;
    const nextValue = value.slice(0, cursor - 2) + "  " + value.slice(cursor);
    this.nameInput.value = nextValue;
    this.nameInput.setSelectionRange(cursor, cursor);
  }

  insertTextAtCursor(text) {
    if (!this.nameInput) return;
    const selectionStart = this.nameInput.selectionStart ?? this.nameInput.value.length;
    const selectionEnd = this.nameInput.selectionEnd ?? this.nameInput.value.length;
    const value = this.nameInput.value;
    const nextValue = value.slice(0, selectionStart) + text + value.slice(selectionEnd);
    this.nameInput.value = nextValue;
    const nextCursor = selectionStart + text.length;
    this.nameInput.setSelectionRange(nextCursor, nextCursor);
    this.lastNameInputValue = this.nameInput.value;
    this.updateNameButtonState();
  }

  async handleSaveImage() {
    if (this.isSaving || !this.snapshot || !this.nameLocked) return;
    this.setActionButtonsState(true, "save");
    try {
      const blob = await this.captureReportBlob();
      if (!blob) return;
      this.downloadBlob(blob, "wavemaker-progress-report.png");
    } catch {
      // Ignore download failures.
    } finally {
      this.setActionButtonsState(false, "save");
    }
  }

  async handleCopyImage() {
    if (!this.clipboardSupported || this.isSaving || !this.snapshot || !this.nameLocked) return;
    this.setActionButtonsState(true, "copy");
    let copied = false;
    try {
      const blob = await this.captureReportBlob();
      if (!blob) return;
      await this.writeToClipboard(blob);
      copied = true;
      this.showCopySuccess();
    } finally {
      this.setActionButtonsState(false, "copy", !copied);
    }
  }

  async captureReportBlob() {
    if (!this.snapshot || !this.nameLocked) return null;
    if (typeof document === "undefined") return null;
    await this.waitForFonts();
    const canvas = this.buildExportCanvas();
    if (!canvas) return null;
    return this.canvasToBlob(canvas);
  }

  async renderReportCanvas() {
    if (!this.snapshot || !this.nameLocked || !this.reportCanvas || !this.reportWrapper) {
      this.updateDialogScale();
      return;
    }
    if (typeof window === "undefined") return;
    const width = Math.round(this.reportWrapper.offsetWidth);
    const layout = this.getSummaryLayout(width, this.snapshot);
    const height = Math.round(layout.summaryHeight);
    this.reportWrapper.style.height = `${height}px`;
    if (width <= 0 || height <= 0) {
      this.updateDialogScale();
      return;
    }
    await this.waitForFonts();
    const scale = Math.max(1, window.devicePixelRatio || 1);
    const context = this.prepareCanvas(this.reportCanvas, width, height, scale, true);
    if (!context) {
      this.updateDialogScale();
      return;
    }
    context.clearRect(0, 0, width, height);
    this.drawReportSections(context, 0, 0, width, height, this.snapshot);
    this.updateDialogScale();
  }

  buildExportCanvas() {
    if (!this.snapshot || !this.content || !this.reportWrapper || !this.header || !this.nameRow) {
      return null;
    }
    if (typeof window === "undefined") return null;
    const layout = this.getContentLayout();
    if (!layout) return null;
    const headerHeight = Math.round(this.header.offsetHeight || this.header.getBoundingClientRect().height);
    const nameRowHeight = Math.round(this.nameRow.offsetHeight || this.nameRow.getBoundingClientRect().height);
    const summaryLayout = this.getSummaryLayout(layout.innerWidth, this.snapshot);
    const reportHeight = Math.round(summaryLayout.summaryHeight);
    const height = Math.max(
      1,
      Math.round(
        layout.paddingTop +
          headerHeight +
          layout.gap +
          nameRowHeight +
          layout.gap +
          reportHeight +
          layout.paddingBottom,
      ),
    );
    const canvas = document.createElement("canvas");
    const scale = Math.max(1, window.devicePixelRatio || 1);
    const context = this.prepareCanvas(canvas, layout.width, height, scale, false);
    if (!context) return null;
    context.fillStyle = "#f9fbfa";
    context.fillRect(0, 0, layout.width, height);
    let cursorY = layout.paddingTop;
    this.drawHeader(context, layout.paddingLeft, cursorY, layout.innerWidth);
    cursorY += headerHeight + layout.gap;
    this.drawNameRow(context, layout.paddingLeft, cursorY, layout.innerWidth, nameRowHeight);
    cursorY += nameRowHeight + layout.gap;
    this.drawReportSections(
      context,
      layout.paddingLeft,
      cursorY,
      layout.innerWidth,
      reportHeight,
      this.snapshot,
    );
    return canvas;
  }

  getContentLayout() {
    if (!this.content || typeof window === "undefined") return null;
    const rect = this.content.getBoundingClientRect();
    const width = Math.round(this.content.offsetWidth || rect.width);
    if (!width) return null;
    const styles = window.getComputedStyle(this.content);
    const paddingTop = parseFloat(styles.paddingTop || "0");
    const paddingBottom = parseFloat(styles.paddingBottom || "0");
    const paddingLeft = parseFloat(styles.paddingLeft || "0");
    const paddingRight = parseFloat(styles.paddingRight || "0");
    const gap = parseFloat(styles.gap || "0");
    const innerWidth = Math.max(0, width - paddingLeft - paddingRight);
    return {
      width,
      innerWidth,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      gap,
    };
  }

  updateDialogScale() {
    if (!this.dialog || typeof window === "undefined") {
      return;
    }
    const naturalWidth = Math.max(DIALOG_MIN_WIDTH, this.dialog.offsetWidth || DIALOG_MIN_WIDTH);
    const naturalHeight = Math.max(1, this.dialog.offsetHeight || this.dialog.scrollHeight || 1);
    const maxWidth = Math.max(0, Math.floor(window.innerWidth * DIALOG_VIEWPORT_FIT_RATIO));
    const maxHeight = Math.max(0, Math.floor(window.innerHeight * DIALOG_VIEWPORT_FIT_RATIO));
    const widthScale = maxWidth > 0 ? maxWidth / naturalWidth : 1;
    const heightScale = maxHeight > 0 ? maxHeight / naturalHeight : 1;
    const scale = Math.min(1, widthScale, heightScale);
    this.dialog.style.transform = scale < 1 ? `scale(${scale})` : "none";
  }

  prepareCanvas(canvas, width, height, scale, updateStyle) {
    const scaledWidth = Math.max(1, Math.round(width * scale));
    const scaledHeight = Math.max(1, Math.round(height * scale));
    if (canvas.width !== scaledWidth) {
      canvas.width = scaledWidth;
    }
    if (canvas.height !== scaledHeight) {
      canvas.height = scaledHeight;
    }
    if (updateStyle) {
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.setTransform(scale, 0, 0, scale, 0, 0);
    context.imageSmoothingEnabled = true;
    return context;
  }

  drawHeader(context, x, y, width) {
    const translate = getTranslate();
    const title = translate.t("progressReport.title");
    context.save();
    context.fillStyle = "#22312c";
    context.font = `600 26px ${this.getFontFamily()}`;
    context.textAlign = "left";
    context.textBaseline = "top";
    context.fillText(title, x, y, width);
    context.restore();
  }

  drawNameRow(context, x, y, width, height) {
    const translate = getTranslate();
    const label = translate.t("progressReport.nameLabel");
    const name = this.snapshot?.playerName ?? this.nameInput?.value ?? "";
    const gap = 12;
    context.save();
    context.font = `600 14px ${this.getFontFamily()}`;
    context.textBaseline = "middle";
    context.textAlign = "left";
    const labelWidth = context.measureText(label).width;
    const labelY = y + height / 2;
    context.fillStyle = "#2b3a34";
    context.fillText(label, x, labelY);
    const inputX = x + labelWidth + gap;
    const inputWidth = Math.max(0, width - labelWidth - gap);
    this.drawStripedNameBackground(context, inputX, y, inputWidth, height, 8);
    context.fillStyle = "#1f2c27";
    context.font = `500 18px ${this.getFontFamily()}`;
    context.textAlign = "left";
    context.fillText(name, inputX + 12, labelY);
    context.restore();
  }

  drawReportSections(context, x, y, width, height, snapshot) {
    const summaryLayout = this.getSummaryLayout(width, snapshot);
    const summaryHeight = Math.min(height, Math.max(0, summaryLayout.summaryHeight));
    const cardX = x + summaryLayout.cardOffsetX;
    this.drawSummaryPanel(
      context,
      cardX,
      y,
      summaryLayout.cardWidth,
      summaryHeight,
      snapshot,
      summaryLayout.rows,
    );
  }

  getSummaryRows(snapshot) {
    const translate = getTranslate();
    const totals = snapshot?.totals ?? {};
    return [
      {
        label: translate.t("progressReport.summary.timeSpentInGame"),
        value: formatDuration(totals.timeSpentSeconds ?? 0),
      },
      {
        label: translate.t("progressReport.summary.levelsBeaten"),
        value: formatNumber(totals.levelsBeaten ?? 0),
      },
    ];
  }

  getSummaryLayout(width, snapshot) {
    const rows = this.getSummaryRows(snapshot);
    const lineHeight = 18;
    const lineGap = 10;
    const paddingY = 14;
    const titleBlockHeight = 26;
    const rowsHeight = rows.length * lineHeight + Math.max(0, rows.length - 1) * lineGap;
    const contentHeight = paddingY + titleBlockHeight + rowsHeight + paddingY;
    const strokeInset = 1;
    const summaryHeight = Math.max(0, Math.round(contentHeight + strokeInset * 2));
    const cardWidth = Math.max(0, Math.round(width - strokeInset * 2));
    const cardOffsetX = Math.max(0, strokeInset);
    return {
      rows,
      summaryHeight,
      cardWidth,
      cardOffsetX,
    };
  }

  drawSummaryPanel(context, x, y, width, height, snapshot, rows = null) {
    const radius = 14;
    const paddingX = 18;
    const paddingY = 14;
    const gradient = context.createLinearGradient(x, y, x + width, y + height);
    gradient.addColorStop(0, "rgba(85, 156, 142, 0.16)");
    gradient.addColorStop(1, "rgba(90, 138, 199, 0.12)");
    this.fillRoundedRect(context, x, y, width, height, radius, gradient, "rgba(34, 49, 44, 0.18)");
    context.save();
    context.font = `600 18px ${this.getFontFamily()}`;
    context.fillStyle = "#24352f";
    context.textAlign = "left";
    context.textBaseline = "top";
    const translate = getTranslate();
    context.fillText(translate.t("progressReport.summary.title"), x + paddingX, y + paddingY);
    const lineHeight = 18;
    const lineGap = 10;
    const startY = y + paddingY + 26;
    const summaryRows = rows ?? this.getSummaryRows(snapshot);
    context.font = `600 15px ${this.getFontFamily()}`;
    const labelWidths = summaryRows.map((row) => context.measureText(row.label).width);
    const labelColumnWidth = labelWidths.length ? Math.max(...labelWidths) : 0;
    context.font = `700 15px ${this.getFontFamily()}`;
    const valueWidths = summaryRows.map((row) => context.measureText(row.value).width);
    const valueColumnWidth = valueWidths.length ? Math.max(...valueWidths) : 0;
    const labelGap = 10;
    const blockWidth = labelColumnWidth + labelGap + valueColumnWidth;
    const availableWidth = Math.max(0, width - paddingX * 2);
    const blockStartX = x + paddingX + Math.max(0, (availableWidth - blockWidth) / 2);
    const labelRightX = blockStartX + labelColumnWidth;
    const valueX = labelRightX + labelGap;
    summaryRows.forEach((row, index) => {
      const lineY = startY + index * (lineHeight + lineGap);
      this.drawMetricLine(context, row.label, row.value, labelRightX, valueX, lineY);
    });
    context.restore();
  }

  drawMetricLine(context, label, value, labelRightX, valueX, y) {
    context.font = `600 15px ${this.getFontFamily()}`;
    context.fillStyle = "rgba(36, 53, 47, 0.9)";
    context.textAlign = "right";
    context.textBaseline = "top";
    context.fillText(label, labelRightX, y);
    context.font = `700 15px ${this.getFontFamily()}`;
    context.fillStyle = "#203029";
    context.textAlign = "left";
    context.fillText(value, valueX, y);
  }

  drawStripedNameBackground(context, x, y, width, height, radius) {
    if (width <= 0 || height <= 0) return;
    context.save();
    this.roundedRectPath(context, x, y, width, height, radius);
    context.clip();
    const gradient = context.createLinearGradient(0, y, 0, y + height);
    gradient.addColorStop(0, "#ffffff");
    gradient.addColorStop(1, "#f0f4f2");
    context.fillStyle = gradient;
    context.fillRect(x, y, width, height);

    const stripeWidth = 10;
    const diagonal = Math.sqrt(width ** 2 + height ** 2);
    const buffer = stripeWidth * 2;
    context.save();
    context.translate(x + width / 2, y + height / 2);
    context.rotate((45 * Math.PI) / 180);
    const start = -diagonal / 2 - buffer;
    const end = diagonal / 2 + buffer;
    for (let offset = start, index = 0; offset < end; offset += stripeWidth, index += 1) {
      context.fillStyle = index % 2 === 0 ? "rgba(232, 239, 236, 0.9)" : "rgba(220, 231, 226, 0.9)";
      context.fillRect(offset, -diagonal / 2 - buffer, stripeWidth, diagonal + buffer * 2);
    }
    context.restore();
    context.restore();

    context.save();
    context.strokeStyle = "rgba(34, 49, 44, 0.2)";
    context.lineWidth = 1;
    this.roundedRectPath(context, x, y, width, height, radius);
    context.stroke();
    context.restore();
  }

  fillRoundedRect(context, x, y, width, height, radius, fillStyle, strokeStyle) {
    context.save();
    const inset = strokeStyle ? 0.5 : 0;
    const nextX = x + inset;
    const nextY = y + inset;
    const nextW = Math.max(0, width - inset * 2);
    const nextH = Math.max(0, height - inset * 2);
    const nextRadius = Math.max(0, radius - inset);
    this.roundedRectPath(context, nextX, nextY, nextW, nextH, nextRadius);
    context.fillStyle = fillStyle;
    context.fill();
    if (strokeStyle) {
      context.strokeStyle = strokeStyle;
      context.lineWidth = 1;
      this.roundedRectPath(context, nextX, nextY, nextW, nextH, nextRadius);
      context.stroke();
    }
    context.restore();
  }

  roundedRectPath(context, x, y, width, height, radius) {
    const clamped = Math.max(0, Math.min(radius, Math.min(width, height) / 2));
    context.beginPath();
    context.moveTo(x + clamped, y);
    context.arcTo(x + width, y, x + width, y + height, clamped);
    context.arcTo(x + width, y + height, x, y + height, clamped);
    context.arcTo(x, y + height, x, y, clamped);
    context.arcTo(x, y, x + width, y, clamped);
    context.closePath();
  }

  getFontFamily() {
    return '"Avenir Next", "Avenir", "Helvetica Neue", Arial, sans-serif';
  }

  async waitForFonts() {
    if (typeof document === "undefined") return;
    const fontSet = document.fonts;
    if (!fontSet?.ready) return;
    try {
      await fontSet.ready;
    } catch {
      // Ignore font loading failures and continue with capture.
    }
  }

  canvasToBlob(canvas) {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/png");
    });
  }

  downloadBlob(blob, filename) {
    if (typeof document === "undefined") return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (typeof window !== "undefined") {
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  }

  setActionButtonsState(saving, source, restoreLabel = true) {
    this.isSaving = saving;
    if (saving) {
      const buttons = [this.saveButton, this.copyButton].filter(Boolean);
      buttons.forEach((button) => {
        button.disabled = true;
        button.setAttribute("aria-disabled", "true");
      });
    } else {
      this.updateActionButtonsAvailability();
    }
    const target = source === "save" ? this.saveButton : this.copyButton;
    if (!target) return;
    if (saving) {
      target.setAttribute("data-label", target.textContent ?? "");
      target.textContent = getTranslate().t("progressReport.preparingImage");
      return;
    }
    if (!restoreLabel) return;
    if (target.hasAttribute("data-label")) {
      const label = target.getAttribute("data-label");
      if (label) {
        target.textContent = label;
      }
      target.removeAttribute("data-label");
    }
  }

  checkClipboardSupport() {
    if (typeof window === "undefined") return false;
    const clipboard = navigator.clipboard;
    return Boolean(clipboard && typeof window.ClipboardItem !== "undefined");
  }

  async writeToClipboard(blob) {
    if (typeof window === "undefined") return;
    const ClipboardItemCtor = window.ClipboardItem;
    const item = new ClipboardItemCtor({ "image/png": blob });
    await navigator.clipboard.write([item]);
  }

  showCopySuccess() {
    if (typeof window === "undefined" || !this.copyButton) return;
    if (this.copyFeedbackTimeout) {
      window.clearTimeout(this.copyFeedbackTimeout);
    }
    if (!this.copyButton.hasAttribute("data-label")) {
      this.copyButton.setAttribute("data-label", this.copyButton.textContent ?? "");
    }
    this.copyButton.textContent = getTranslate().t("progressReport.copyFeedback");
    this.copyFeedbackTimeout = window.setTimeout(() => {
      if (!this.copyButton) return;
      const label = this.copyButton.getAttribute("data-label");
      if (label) {
        this.copyButton.textContent = label;
      }
      this.copyButton.removeAttribute("data-label");
      this.copyFeedbackTimeout = null;
    }, 2800);
  }
}
